import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  type FamilyManagementService, type FamilyProfile, type FamilyProfilesState, type NewFamilyMember,
  familyManagementService,
} from '../familyManagement';
import { SidebarIcon } from './SidebarIcon';
import { AddFamilyMemberDialog } from './AddFamilyMemberDialog';
import { FamilyDialog } from './FamilyDialog';
import { useFamilyFocusMode } from './useFamilyFocusMode';
import './FamilyProfiles.css';

const Plus = () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M12 5v14" /></svg>;
const Check = () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg>;
const Close = () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 7 10 10M7 17 17 7" /></svg>;
const initials = (name: string) => name.trim().split(/\s+/).filter(Boolean)
  .filter((_, index, parts) => index === 0 || index === parts.length - 1)
  .map((part) => Array.from(part)[0]).join('').toLocaleUpperCase('de-CH');

const primaryState = (id: string, fullName: string): FamilyProfilesState => ({
  profiles: [{ id, fullName, isPrimary: true, canActivate: true, canRemove: false }],
  activeProfileId: id,
  canAdd: false,
});

function checkProfiles(state: FamilyProfilesState) {
  const primary = state.profiles.filter((profile) => profile.isPrimary);
  if (primary.length !== 1 || new Set(state.profiles.map((profile) => profile.id)).size !== state.profiles.length ||
      !state.profiles.every((profile) => profile.id && profile.fullName.trim()) ||
      !state.profiles.some((profile) => profile.id === state.activeProfileId && profile.canActivate)) {
    throw new Error('Invalid family profile response');
  }
  return state;
}

function useFamilyProfiles({ primaryName, primaryProfileId, service = familyManagementService }: {
  primaryName: string;
  primaryProfileId: string;
  service?: FamilyManagementService;
}) {
  const [state, setState] = useState(() => primaryState(primaryProfileId, primaryName));
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState<'add' | FamilyProfile | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [removeError, setRemoveError] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const removeRef = useRef<HTMLButtonElement | null>(null);
  const operationInProgress = useRef(false);
  const mounted = useRef(true);
  const generation = useRef(0);
  const active = state.profiles.find((profile) => profile.id === state.activeProfileId)!;
  const hasFamilyMembers = state.profiles.some((profile) => !profile.isPrimary);
  useFamilyFocusMode(containerRef);

  useEffect(() => {
    mounted.current = true;
    generation.current += 1;
    const controller = new AbortController();
    setState(primaryState(primaryProfileId, primaryName));
    setOpen(false);
    setModal(null);
    setError('');
    setNotice('');
    setBusy(false);
    setLoading(false);
    operationInProgress.current = false;
    if (!service.unavailableReason) {
      setLoading(true);
      service.loadProfiles(controller.signal).then(checkProfiles).then((profiles) => {
        if (!controller.signal.aborted) setState(profiles);
      }).catch(() => {
        if (!controller.signal.aborted) setError('Die verfügbaren Profile konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
      }).finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    }
    return () => { mounted.current = false; generation.current += 1; controller.abort(); };
  }, [primaryName, primaryProfileId, service]);

  useEffect(() => {
    if (!open) return;
    const dismiss = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setOpen(false); toggleRef.current?.focus(); }
    };
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', dismiss);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  const openAdd = () => {
    setOpen(false);
    setNotice('');
    setModal('add');
  };

  const add = async (input: NewFamilyMember) => {
    if (!state.canAdd || service.unavailableReason) throw new Error('Not authorized');
    const operationGeneration = generation.current;
    const profile = await service.addMember(input);
    if (profile.isPrimary || !profile.id || !profile.fullName?.trim() || state.profiles.some((item) => item.id === profile.id)) {
      throw new Error('Invalid family member response');
    }
    if (!mounted.current || generation.current !== operationGeneration) return;
    // Adding a verified link never changes the active profile.
    setState((current) => ({ ...current, profiles: [...current.profiles, profile] }));
    setNotice('Das Familienmitglied wurde hinzugefügt. Ihr aktives Profil bleibt bestehen.');
  };

  const activate = async (profile: FamilyProfile) => {
    if (operationInProgress.current) return;
    if (profile.id === state.activeProfileId) { setOpen(false); toggleRef.current?.focus(); return; }
    if (!profile.canActivate || service.unavailableReason) return;
    operationInProgress.current = true;
    const operationGeneration = generation.current;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await service.activateProfile(profile.id);
      if (!mounted.current || generation.current !== operationGeneration) return;
      setState((current) => ({ ...current, activeProfileId: profile.id }));
      setOpen(false);
      toggleRef.current?.focus();
    } catch {
      if (mounted.current && generation.current === operationGeneration) setError('Das Profil konnte nicht gewechselt werden. Ihr bisheriges Profil bleibt aktiv.');
    } finally {
      if (generation.current === operationGeneration) {
        operationInProgress.current = false;
        if (mounted.current) setBusy(false);
      }
    }
  };

  const remove = async (profile: FamilyProfile) => {
    if (operationInProgress.current || profile.isPrimary || !profile.canRemove || service.unavailableReason) return;
    operationInProgress.current = true;
    const operationGeneration = generation.current;
    setBusy(true);
    setRemoveError('');
    try {
      await service.removeMember(profile.id);
      if (!mounted.current || generation.current !== operationGeneration) return;
      setState((current) => ({
        ...current, profiles: current.profiles.filter((item) => item.id !== profile.id),
        activeProfileId: current.activeProfileId === profile.id
          ? current.profiles.find((item) => item.isPrimary)!.id : current.activeProfileId,
      }));
      setModal(null);
      setOpen(false);
      setNotice('Die Familienverknüpfung wurde entfernt. Patientenakte und Termine bleiben erhalten.');
    } catch {
      if (mounted.current && generation.current === operationGeneration) setRemoveError('Das Familienmitglied konnte nicht entfernt werden. Die Verknüpfung bleibt bestehen. Bitte versuchen Sie es erneut.');
    } finally {
      if (generation.current === operationGeneration) {
        operationInProgress.current = false;
        if (mounted.current) setBusy(false);
      }
    }
  };

  return {
    state, active, hasFamilyMembers, open, setOpen, modal, setModal, busy, loading, error, notice,
    removeError, setRemoveError, containerRef, toggleRef, addRef, removeRef, service, openAdd, add, activate, remove,
  };
}

const FamilyProfilesContext = createContext<ReturnType<typeof useFamilyProfiles> | null>(null);

// The sidebar and the profile section share the original controller and in-memory data.
export function FamilyProfilesProvider({ children, primaryName = localStorage.getItem('userFullName')?.trim()
  || localStorage.getItem('loginUserName')?.trim() || 'Benutzer',
  primaryProfileId = localStorage.getItem('loginPatientId')?.trim() || 'primary', service = familyManagementService }: {
  children: ReactNode;
  primaryName?: string;
  primaryProfileId?: string;
  service?: FamilyManagementService;
}) {
  // A patient-context switch must not redefine the account's primary profile.
  const accountProfileId = useRef(primaryProfileId);
  const family = useFamilyProfiles({ primaryName, primaryProfileId: accountProfileId.current, service });
  const { modal, setModal, busy, state, add, addRef, removeRef, removeError, remove } = family;
  return (
    <FamilyProfilesContext.Provider value={family}>
      {children}
      {modal === 'add' && <AddFamilyMemberDialog onAdd={add} returnFocusRef={addRef}
        unavailableReason={service.unavailableReason || (!state.canAdd ? 'Sie haben keine Berechtigung, Familienmitglieder hinzuzufügen.' : undefined)}
        onClose={() => setModal(null)} />}
      {modal && modal !== 'add' && (
        <FamilyDialog title="Familienmitglied entfernen?" busy={busy}
          returnFocusRef={removeRef}
          fallbackFocusRef={addRef}
          description={`Möchten Sie ${modal.fullName} wirklich aus Ihrer Familienverwaltung entfernen?`}
          onClose={() => setModal(null)}>
          <p className="family-dialog__notice">Die Patientenakte und bestehende Termine werden dadurch nicht gelöscht.</p>
          {removeError && <p role="alert" className="family-dialog__error">{removeError}</p>}
          <div className="family-dialog__actions">
            <button type="button" className="family-dialog__secondary" data-initial-focus="" disabled={busy}
              onClick={() => setModal(null)}>Abbrechen</button>
            <button type="button" className="family-dialog__caution" disabled={busy} onClick={() => remove(modal)}>
              {busy ? 'Wird entfernt…' : 'Familienmitglied entfernen'}
            </button>
          </div>
        </FamilyDialog>
      )}
    </FamilyProfilesContext.Provider>
  );
}

export function FamilyProfiles({ primaryName, primaryProfileId, onLogout, service = familyManagementService }: {
  primaryName: string;
  primaryProfileId: string;
  onLogout: () => void;
  service?: FamilyManagementService;
}) {
  const family = useContext(FamilyProfilesContext);
  if (!family) {
    return <FamilyProfilesProvider primaryName={primaryName} primaryProfileId={primaryProfileId} service={service}>
      <FamilyProfiles primaryName={primaryName} primaryProfileId={primaryProfileId} onLogout={onLogout} />
    </FamilyProfilesProvider>;
  }
  const { state, active, hasFamilyMembers, open, setOpen, busy, loading, error, containerRef, toggleRef, activate } = family;
  return (
    <div className="family-profiles" ref={containerRef}>
      {error && <p role="alert" className="family-profiles__error">{error}</p>}
      <div className="family-profiles__anchor">
        {open && hasFamilyMembers && (
          <section id="family-profile-options" className="family-profiles__dropdown" aria-labelledby="family-profile-heading">
            <h2 id="family-profile-heading">PROFIL WECHSELN</h2>
            <ul className="family-profiles__list">
              {state.profiles.map((profile) => (
                <li key={profile.id} className={profile.id === active.id ? 'family-profiles__row family-profiles__row--active' : 'family-profiles__row'}>
                  <button type="button" className="family-profiles__select" onClick={() => activate(profile)}
                    disabled={busy || !profile.canActivate} aria-pressed={profile.id === active.id}
                    aria-label={profile.fullName} title={!profile.canActivate ? 'Freigabe durch die Praxis erforderlich' : undefined}>
                    <span className="family-profiles__initials" aria-hidden="true">{initials(profile.fullName)}</span>
                    <span className="family-profiles__name">{profile.fullName}</span>
                    {profile.id === active.id && <span className="family-profiles__check"><Check /></span>}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
        <div className={`family-profiles__tile${hasFamilyMembers ? '' : ' family-profiles__tile--single'}`}>
          <span className="family-profiles__avatar"><SidebarIcon name="profile" /></span>
          <div className="family-profiles__identity">{hasFamilyMembers && <span>Aktives Profil</span>}
            <strong>{family.service.unavailableReason ? primaryName : active.fullName}</strong></div>
          {hasFamilyMembers && <button ref={toggleRef} type="button" className="family-profiles__toggle" aria-label="Profilauswahl"
            disabled={busy || loading} aria-expanded={open} aria-controls={open ? 'family-profile-options' : undefined}
            onClick={() => setOpen((current) => !current)}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 10 5 5 5-5" /></svg>
          </button>}
          <button type="button" className="family-profiles__logout" aria-label="Abmelden" onClick={onLogout} disabled={busy}>
            <SidebarIcon name="logout" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function FamilyMemberAddButton() {
  const family = useContext(FamilyProfilesContext);
  const ref = useRef<HTMLDivElement>(null);
  useFamilyFocusMode(ref);
  if (!family) return null;
  return <div className="family-profiles family-profiles--management family-profiles--header" ref={ref}>
    <button ref={family.addRef} type="button" className="family-profiles__add" onClick={family.openAdd}
      disabled={family.busy || family.loading} aria-haspopup="dialog"><Plus /><span>Familienmitglied hinzufügen</span></button>
  </div>;
}

export function FamilyMembers() {
  const family = useContext(FamilyProfilesContext);
  const ref = useRef<HTMLDivElement>(null);
  useFamilyFocusMode(ref);
  if (!family) return null;
  const { state, busy, loading, notice, service, removeRef, setOpen, setModal, setRemoveError } = family;
  const members = state.profiles.filter((profile) => !profile.isPrimary);
  return (
    <div className="family-profiles family-profiles--management" ref={ref}>
      {notice && <p role="status" className="family-profiles__notice">{notice}</p>}
      {service.unavailableReason ? <p className="family-profiles__availability">{service.unavailableReason}</p>
        : loading ? <p className="family-profiles__availability">Familienmitglieder werden geladen…</p>
          : !members.length && <p className="family-profiles__availability">Noch keine Familienmitglieder hinzugefügt.</p>}
      <ul className="family-profiles__list">
        {members.map((profile) => (
          <li key={profile.id} className="family-profiles__row">
            <span className="family-profiles__initials" aria-hidden="true">{initials(profile.fullName)}</span>
            <span className="family-profiles__name">{profile.fullName}</span>
            {!profile.canActivate && <span className="family-profiles__availability">Freigabe durch die Praxis erforderlich</span>}
            {profile.canRemove && <button type="button" className="family-profiles__remove" disabled={busy}
              aria-label={`${profile.fullName} entfernen`} onClick={(event) => {
                removeRef.current = event.currentTarget;
                setRemoveError(''); setOpen(false); setModal(profile);
              }}><Close /></button>}
          </li>
        ))}
      </ul>
    </div>
  );
}
